import React, { useContext, useEffect, useState, useMemo } from "react";
import { Box } from "@mui/material";
import { formatDate } from "features/helpers/utils";
import { analysisColors } from "features/configure/chartColors";
import axios from "axios";
import useDispatchSnackbar from "hooks/useDispatchSnackbar";
import { KeyMatrixContext } from "../context/KeyMatrixProvider";
import { useOutletContext } from "react-router";
import { apiUrl } from "features/configure";
import { useIntl } from "react-intl";
import DualYAxisChartContainer from "components/DualYAxisChart/DualYAxisChartContainer";
import { max } from "lodash";

export default function WalletAnalysisChart({
  dynamicEntityName,
  dynamicApiUrl,
}) {
  const [walletAnalysis, setWalletAnalysis] = useState();
  const [selectedYAxis, setSelectedYAxis] = useState([
    "totalWallets",
    "newWallets",
  ]);
  const { formatMessage } = useIntl();
  const [entityName] = useOutletContext();
  const { granularity, range, formatLabel, isLoading, setIsLoading } =
    useContext(KeyMatrixContext);
  const dispatchSnackbar = useDispatchSnackbar();
  async function fetchWalletAnalysis() {
    try {
      setIsLoading((prev) => ({ ...prev, walletAnalysisChart: true }));
      const { data } = await axios.post(
        `${apiUrl}/${dynamicApiUrl}/dashboard/${dynamicEntityName}/walletAnalysis`,
        {
          granularity,
          range,
        }
      );
      setWalletAnalysis(data);
    } catch (err) {
      dispatchSnackbar({ err });
    } finally {
      setIsLoading((prev) => ({ ...prev, walletAnalysisChart: false }));
    }
  }
  useEffect(() => {
    fetchWalletAnalysis();
  }, [granularity, range]);

  const totalStats = [
    {
      title: formatMessage({ id: "global.legend.totalWallet" }),
      key: "totalWallets",
      value: walletAnalysis?.totalWalletSum,
    },
    {
      title: formatMessage({ id: "global.legend.newWallet" }),
      key: "newWallets",
      value: walletAnalysis?.newWalletSum,
    },
    {
      title: formatMessage({ id: "global.legend.returnWallet" }),
      key: "returningWallets",
      value: walletAnalysis?.returningWalletSum,
    },
  ];

  const isNoData =
    !walletAnalysis ||
    (walletAnalysis?.newWalletSum === 0 &&
      walletAnalysis?.returningWalletSum === 0 &&
      walletAnalysis?.totalWalletSum === 0);

  const xAxisData = walletAnalysis?.data.map((item) =>
    formatDate(item.date, { month: true })
  );
  const yAxisInfo = [
    {
      label: formatMessage({ id: "global.legend.totalWallet" }),
      key: "totalWallets",
      name: formatMessage({ id: "global.legend.totalWallet" }),
    },
    {
      label: formatMessage({ id: "global.legend.newWallet" }),
      key: "newWallets",
      name: formatMessage({ id: "global.legend.newWallet" }),
    },
    {
      label: formatMessage({ id: "global.legend.returnWallet" }),
      key: "returningWallets",
      name: formatMessage({ id: "global.legend.returnWallet" }),
    },
  ];
  const chartInfos = [
    {
      title: "websiteTraffic.chart.new&returnWallet",
      showYAxisDropdown: true,
      type: "line",
      hasData: !isNoData,
    },
  ];
  const walletAnalysisData = useMemo(() => {
    if (!walletAnalysis)
      return {
        xAxisData: [],
        yAxisData: {},
      };

    return {
      xAxisData,
      yAxisData: { lineChartData: walletAnalysis.data },
    };
  }, [walletAnalysis, xAxisData]);

  return (
    <Box bgcolor="white" p="16px" color="#555" borderRadius="4px">
      <DualYAxisChartContainer
        data={walletAnalysisData}
        yAxisInfos={yAxisInfo}
        selectedYAxis={selectedYAxis}
        setSelectedYAxis={setSelectedYAxis}
        colors={analysisColors.small}
        chartInfos={chartInfos}
        isLoading={isLoading.walletAnalysisChart}
        totalStats={totalStats}
        customStyle={{
          echartStyle: { height: "269px" },
          dropdownStyle: {
            width: "100%",
          },
        }}
        otherCustomSetting={{
          formatXAxisLabel: { open: true, format: "DD MMM" },
        }}
      />
    </Box>
  );
}
